import { FC } from 'react';
import * as yup from 'yup';
import { Dialog, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { FormSelect } from 'shared/ui/Select/FormSelect';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Paper } from 'shared/ui/Paper';
import { Button } from 'shared/ui/Button';
import { GenerativeLabelsType } from 'entities/Files/types';
import { useFileEditorStore } from 'store/file-editor';

import StringLabel from './string-label';
import TableLabels from './table-labels';

interface ManualModalPropsType {
	labelsDetails?: GenerativeLabelsType & {
		labelIndex: number;
	};
	open: boolean;
	handleClose: () => void;
}
const GenerativeLabelValidationSchema = yup.object({
	name: yup
		.string()
		.required('files.research_required')
		.matches(/^[A-Za-zА-Яа-я0-9_]+$/, 'shared_texts.no_spaces'),

	description: yup.string(),

	type: yup.string().required(),

	columns: yup.array().when('type', {
		is: 'table',
		then: (schema) =>
			schema.of(
				yup.object({
					name: yup.string().required('shared_texts.field_is_required'),
					type: yup.string().required(),
					description: yup.string(),
				}),
			),
		otherwise: (schema) => schema.notRequired(),
	}),
});

const GenerativeLabelsModal: FC<ManualModalPropsType> = ({ labelsDetails, open, handleClose }) => {
	const { t } = useTranslation();
	const { setFileDetailsV2, fileDetails } = useFileEditorStore();

	const handleManualModalSubmit = (label: GenerativeLabelsType) => {
		if (fileDetails) {
			const labels = labelsDetails
				? fileDetails.labels.map((item, idx) => (idx === labelsDetails.labelIndex ? label : item))
				: [...fileDetails.labels, label];

			setFileDetailsV2({ ...fileDetails, labels });
			handleClose();
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			PaperComponent={Paper}
			slotProps={{
				paper: { title: t(labelsDetails ? 'files.edit_searches' : 'files.add_searches') as string },
			}}
			sx={{
				'width': '600px',
				'margin': '0 auto',
				'& .MuiPaper-root': {
					minWidth: '100%',
				},
			}}
		>
			<Typography variant={'body1'} color={ColorPalette.baliHai} sx={{ marginBottom: '34px' }}>
				{t('files.search_description')}
			</Typography>
			<Formik
				initialValues={{
					name: labelsDetails?.name || '',
					type: labelsDetails?.type || 'string',
					description: labelsDetails?.description || '',
					columns: labelsDetails?.columns?.length
						? labelsDetails.columns
						: [
								{
									name: '',
									type: 'string',
									description: '',
								},
						  ],
				}}
				validationSchema={GenerativeLabelValidationSchema}
				onSubmit={handleManualModalSubmit}
			>
				{({ values, setFieldValue }) => (
					<Form>
						<FormSelect
							name={'type'}
							label={t('shared_texts.label_type') as string}
							placeholder=""
							options={labelTypesOptions}
							value={values.type}
							onSelect={(value) => setFieldValue('type', value)}
							sxError={{ margin: 0 }}
						/>
						<StringLabel />
						{values.type === 'table' ? (
							<TableLabels columns={values.columns || []} onChange={setFieldValue} />
						) : null}

						<Button type={'submit'} fullWidth>
							{labelsDetails ? t('shared_texts.edit') : t('shared_texts.add')}
						</Button>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default GenerativeLabelsModal;

const labelTypesOptions = [
	{ value: 'string', label: 'shared_texts.string' },
	{ value: 'number', label: 'shared_texts.number' },
	{ value: 'table', label: 'shared_texts.table' },
];
